<template>
  <dashboard-panel
    title="Discussions"
    icon="fa-comment"
    :tabs="canAccess() ? tabs : []"
    :active-tab="mine_only == 1 ? 'you' : 'all'"
    :count="count"
    add-label="start a discussion"
    :add-permissions="['can_add_discussions']"
    filterable
    :can-export="count > 0"
    :downloading="loadingDownload"
    :adding-to-vault="loadingAddToVault"
    @download="downloadDiscussion"
    @add-to-vault="addToVault"
    :loading="fetching"
    :empty="!discussions.total"
    empty-text="No discussions found"
    :pager="discussions"
    :busy="busy"
    busy-text="Sending, please wait..."
    @tab="selectTab"
    @add="$refs.addDiscussion.$refs.modal.open()"
    @page="goToPage"
  >
    <template #filter>
      <label class="block text-xxs uppercase tracking-wide text-grey-dark font-semibold mb-1">Date from / to</label>
      <div class="flex items-center">
        <flat-pickr
          class="appearance-none border rounded w-full h-10 px-3 text-sm"
          placeholder="Select date"
          ref="filter"
          v-model="selected_date"
          :config="filter_config"
          :events="[
            'on-change',
            'on-close',
            'on-destroy',
            'on-key-down',
            'on-month-change',
            'on-open',
            'on-parse-config',
            'on-ready',
            'on-value-update',
          ]"
        ></flat-pickr>
        <button
          type="button"
          class="ml-2 px-2 text-grey-dark hover:text-red"
          title="Clear"
          @click.prevent="$refs.filter.fp.clear()"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
    </template>

    <dashboard-list-item
      v-for="discussion in discussions.data"
      :key="discussion.id"
      :author="authorName(discussion)"
      :date="discussion.updated_at"
      :title="discussion.title"
      :body="
        discussion.last_message
          ? discussion.last_message.content
          : 'No messages yet'
      "
      @click="viewDiscussion(discussion)"
    ></dashboard-list-item>

    <template #modals>
      <add-discussion
        :vault="vault"
        :vaultUsers="vaultUsers"
        @sending="sending"
        @getDiscussions="getDiscussions"
        ref="addDiscussion"
      ></add-discussion>

      <view-discussion
        :selected_discussion="selected_discussion"
        :user="user"
        :fetch-messages="fetchMessages"
        @sending="sending"
        @getDiscussions="getDiscussions"
        @view-discussion="viewDiscussion"
        ref="viewDiscussion"
      ></view-discussion>
    </template>
  </dashboard-panel>
</template>

<script>
import { mapGetters } from "vuex";
import flatPickr from "vue-flatpickr-component";
import moment from "moment";
import DashboardPanel from "./DashboardPanel.vue";
import DashboardListItem from "./DashboardListItem.vue";
import AddDiscussion from "./Discussions/Add.vue";
import ViewDiscussion from "./Discussions/View.vue";

export default {
  props: ["vault-users"],

  components: {
    DashboardPanel,
    DashboardListItem,
    flatPickr,
    AddDiscussion,
    ViewDiscussion,
  },

  data() {
    return {
      discussions: {},
      fetching: false,
      busy: false,

      mine_only: 1,
      count: 0,
      tabs: [
        { key: "you", label: "You" },
        { key: "all", label: "All" },
      ],

      selected_discussion: {
        discussion: {},
        raw_messages: [],
        messages_pagination: {},
        page: 1,
        files: [],
      },

      search_params: {
        page: 1,
        items_per_page: 10,
      },

      loadingDownload: false,
      loadingAddToVault: false,

      selected_date: "",
      filter: {
        start_date: "",
        end_date: "",
      },
      filter_config: {
        mode: "range",
        wrap: true,
        allowInput: false,
        dateFormat: "d/m/Y",
        monthSelectorType: "static",
        onChange: (selectedDates) => {
          if (selectedDates.length == 0) {
            this.filter = {
              start_date: "",
              end_date: "",
            };
            this.getDiscussions();
          } else {
            if (selectedDates.length > 1) {
              this.filter.start_date = selectedDates[0];
              this.filter.end_date = selectedDates[1];
              this.getDiscussions();
            }
          }
        },
      },
    };
  },

  mounted() {
    this.init();
  },

  methods: {
    async init() {
      const userRole = await this.checkVaultUserRole();
      if(userRole == 'userG') {
        this.mine_only = 0;
      }

      this.$ebus.$on("view-discussion", (conversation_id) => {
        this.viewDiscussion(conversation_id);
      });
      this.$ebus.$on("mark-discussion-as-read", (conversation_id) => {
        this.markAsRead(conversation_id).then((data) => {
          this.getDiscussions();
        });
      });
      this.$ebus.$on("refresh-discussions", () => {
        this.getDiscussions();
      });

      this.getDiscussions();
    },
    checkVaultUserRole() {
      return axios
        .get(`/api/vaults/${this.$route.params.vault_id}/my-role`)
        .then(({ data }) => {
          if (data) {
            let vault_id = this.vault.id;
            let role_name = data.name;
            let replace_char = vault_id + "-";

            let role = _.camelCase(role_name.replace(replace_char, ""));

            return role;
          }
        });
    },
    sending(isOpen) {
      this.busy = isOpen;
    },
    selectTab(key) {
      this.mine_only = key == "you" ? 1 : 0;
      this.search_params.page = 1;
      this.getDiscussions();
    },
    goToPage(page) {
      this.search_params.page = page;
      this.getDiscussions();
    },
    downloadDiscussion() {
      this.loadingDownload = true;
      setTimeout(() => {
        let url = `api/vaults/${this.$route.params.vault_id}/discussions/download?mine=${this.mine_only}`;
        if (this.filter != "") {
          const filter = moment(this.filter, "DD/MM/YYYY").format("YYYY-MM-DD");
          url = `api/vaults/${this.$route.params.vault_id}/discussions/download?mine=${this.mine_only}&filter=${filter}`;
        }

        this.$noty.success(`Your download will begin shortly.`);

        window.open(url, "_blank");
        this.loadingDownload = false;
      }, 500);
    },
    addToVault() {
      this.loadingAddToVault = true;
      let url = `api/vaults/${this.$route.params.vault_id}/discussions/add-to-vault?mine=${this.mine_only}`;

      if (this.filter != "") {
        const filter = moment(this.filter, "DD/MM/YYYY").format("YYYY-MM-DD");
        url = `api/vaults/${this.$route.params.vault_id}/discussions/add-to-vault?mine=${this.mine_only}&filter=${filter}`;
      }

      axios
        .post(url)
        .then((response) => {
          if (response && response.data) {
            const data = response.data;

            if (data.success) {
              this.$noty.success(data.message);
              this.$ebus.$emit("refresh-file-and-tag-panel");
            } else {
              this.$noty.error(data.message);
            }
          }
        })
        .catch((error) => {
          this.$noty.error(`Failed to add Discussions to Vault.`);
        })
        .finally(() => {
          this.loadingAddToVault = false;
        });
    },
    getDiscussions() {
      this.fetching = true;
      let url = "";

      if (this.mine_only) {
        url = `/api/vaults/${this.$route.params.vault_id}/discussions/mine`;
      } else {
        url = `/api/vaults/${this.$route.params.vault_id}/discussions`;
      }
      axios
        .get(url, {
          params: {
            ...this.search_params,
            start_date: this.filter.start_date
              ? moment(this.filter.start_date).format("YYYY-MM-DD")
              : null,
            end_date: this.filter.end_date
              ? moment(this.filter.end_date).format("YYYY-MM-DD")
              : null,
          },
        })
        .then(({ data }) => {
          this.discussions = data;
          this.count = data.total;
          this.fetching = false;
        })
        .catch((err) => {
          this.fetching = false;
        });
    },
    // who wrote the last message, else who started the discussion
    authorName(discussion) {
      const user =
        (discussion.last_message && discussion.last_message.user) ||
        discussion.creator;
      if (!user) {
        return "-";
      }
      return `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
    },
    viewDiscussion(discussion) {
      this.selected_discussion.files = discussion.files;
      this.selected_discussion.page = 1;
      this.selected_discussion.discussion = discussion;

      this.fetchMessages(discussion)
        .then(({ data }) => {
          this.selected_discussion.messages_pagination = data;
          this.selected_discussion.raw_messages = data.data;
          this.markAsRead(discussion.id);
          this.$refs.viewDiscussion.$refs.modal.open();
        })
        .then(() => {
          if (
            this.selected_discussion &&
            this.selected_discussion.files &&
            this.selected_discussion.files.length > 0
          ) {
            this.$refs.viewDiscussion.previewFile(
              this.selected_discussion.files[0]
            );
          }
        });
    },
    fetchMessages(discussion) {
      return axios.get(
        `api/vaults/${this.$route.params.vault_id}/discussions/${discussion.id}/messages`,
        {
          params: {
            item_per_page: 10,
            page: this.selected_discussion.page,
          },
        }
      );
    },
    markAsRead(id) {
      return axios.patch(
        `api/vaults/${this.$route.params.vault_id}/messages/${id}/mark-read`
      );
    },
    canAccess() {
      if (_.includes(["userA", "userB", "userC", "userD"], this.role)) {
        return true;
      }
      return false;
    },
  },

  computed: {
    ...mapGetters({
      user: "getUser",
      role: "getUserRole",
      vault: "getCurrentVault",
    }),
  },

  watch: {
    "$route.params.vault_id"() {
      _.delay(() => {
        this.getDiscussions();
      }, 1000);
    },
  },
};
</script>
